import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export interface BreadcrumbItem {
  label: string
  to?: string
}

interface BreadcrumbContextValue {
  items: BreadcrumbItem[] | null
  setItems: (items: BreadcrumbItem[] | null) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined)

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<BreadcrumbItem[] | null>(null)
  const value = useMemo(() => ({ items, setItems }), [items])
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

export function useBreadcrumb(): BreadcrumbContextValue {
  const ctx = useContext(BreadcrumbContext)
  if (!ctx) throw new Error('useBreadcrumb must be used inside <BreadcrumbProvider>')
  return ctx
}

// Page-level helper — sets the breadcrumb on mount, clears on unmount.
// Pass a stable array (memoized or literal) to avoid re-setting every render.
export function useSetBreadcrumb(items: BreadcrumbItem[]): void {
  const { setItems } = useBreadcrumb()
  const key = items.map((i) => `${i.label}|${i.to ?? ''}`).join('>')
  const stableItems = useCallback(() => items, [key]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setItems(stableItems())
    return () => setItems(null)
  }, [setItems, stableItems])
}
